import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { businessDate } from '@evogard/domain';
import { cn } from './cn.js';
import {
  WEEKDAYS,
  addDays,
  addMonths,
  dayLabel,
  monthGrid,
  monthTitle,
  partsOf,
  weekdayOf,
} from './dates.js';
import { Icon } from './Icon.js';

export interface CalendarProps {
  /** `AAAA-MM-DD`, ou vazio. É o dia marcado, e o mês que abre. */
  value: string;
  /** Recebe o dia escolhido em `AAAA-MM-DD`. */
  onSelect: (iso: string) => void;
  /**
   * Põe o foco no dia assim que monta — o que o `DateField` quer ao abrir o
   * calendário, e o que uma tela com o calendário solto não quer.
   */
  autoFocus?: boolean;
  /**
   * Quais dias aparecem marcados, quando são vários — o `CalendarSelect`.
   * Sem ele, o marcado é só `value`.
   */
  isMarked?: (iso: string) => boolean;
}

/**
 * O mês, para escolher uma data olhando o calendário.
 *
 * ## Por que não o do navegador, e por que não uma biblioteca
 *
 * O do navegador é o defeito que o `DateField` fechou: ele fala a língua da
 * máquina e não a da página, e não escurece com o tema. E o que ele faz é
 * pouco — uma grade de 42 botões e oito teclas —, o que não paga uma
 * dependência que traria a própria folha de estilo para ser desmontada.
 *
 * ## O teclado é o da APG (o padrão de grade de datas da W3C)
 *
 * Um dia só é tabulável por vez (o foco "rola" pela grade): setas andam um dia
 * ou uma semana, `Home`/`End` vão ao começo e ao fim da semana, `PageUp`/
 * `PageDown` trocam de mês (com `Shift`, de ano), `Enter` e espaço escolhem.
 * O mês acompanha o foco — sair do dia 30 pela seta leva ao mês seguinte.
 *
 * "Hoje" é o dia de negócio no fuso da casa (`businessDate`), não o do relógio
 * da máquina: é o mesmo dia que o resto do sistema chama de hoje.
 */
export function Calendar({
  value,
  onSelect,
  autoFocus = false,
  isMarked,
}: CalendarProps) {
  const today = businessDate(new Date());
  const selected = partsOf(value) ? value : '';
  const [cursor, setCursor] = useState(selected || today);
  const gridRef = useRef<HTMLDivElement>(null);
  /* Só puxa o foco quando quem mudou o cursor foi o teclado — senão o clique
   * em "próximo mês" roubaria o foco do botão que acabou de ser clicado. */
  const follow = useRef(autoFocus);

  const { year, month } = partsOf(cursor)!;
  const grid = monthGrid(year, month);

  useEffect(() => {
    if (!follow.current) return;
    follow.current = false;
    gridRef.current?.querySelector<HTMLButtonElement>(`[data-dia="${cursor}"]`)?.focus();
  }, [cursor]);

  const move = (next: string) => {
    follow.current = true;
    setCursor(next);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step: Record<string, () => string> = {
      ArrowLeft: () => addDays(cursor, -1),
      ArrowRight: () => addDays(cursor, 1),
      ArrowUp: () => addDays(cursor, -7),
      ArrowDown: () => addDays(cursor, 7),
      Home: () => addDays(cursor, -weekdayOf(cursor)),
      End: () => addDays(cursor, 6 - weekdayOf(cursor)),
      PageUp: () => addMonths(cursor, e.shiftKey ? -12 : -1),
      PageDown: () => addMonths(cursor, e.shiftKey ? 12 : 1),
    };
    const go = step[e.key];
    if (!go) return;
    e.preventDefault();
    move(go());
  };

  const title = monthTitle(year, month);

  return (
    <div className="flex w-max flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <NavButton
          label="Mês anterior"
          icon="seta-esquerda"
          onClick={() => setCursor(addMonths(cursor, -1))}
        />
        <p
          aria-live="polite"
          className="text-sm font-semibold text-texto first-letter:uppercase"
        >
          {title}
        </p>
        <NavButton
          label="Próximo mês"
          icon="seta-direita"
          onClick={() => setCursor(addMonths(cursor, 1))}
        />
      </div>

      <div ref={gridRef} role="grid" aria-label={title} onKeyDown={onKeyDown}>
        <div role="row" className="grid grid-cols-7">
          {WEEKDAYS.map((w) => (
            <span
              key={w.long}
              role="columnheader"
              aria-label={w.long}
              className="flex h-7 items-center justify-center text-rotulo font-semibold text-texto-suave"
            >
              {w.short}
            </span>
          ))}
        </div>

        {grid.map((week) => (
          <div key={week[0]} role="row" className="grid grid-cols-7">
            {week.map((iso) => {
              const outside = partsOf(iso)!.month !== month;
              const isSelected = isMarked ? isMarked(iso) : iso === selected;
              const isToday = iso === today;
              return (
                <span key={iso} role="gridcell" aria-selected={isSelected}>
                  <button
                    type="button"
                    data-dia={iso}
                    tabIndex={iso === cursor ? 0 : -1}
                    aria-label={dayLabel(iso)}
                    aria-current={isToday ? 'date' : undefined}
                    onClick={() => onSelect(iso)}
                    onFocus={() => iso !== cursor && setCursor(iso)}
                    className={cn(
                      'flex h-9 w-9 items-center justify-center rounded-borda text-xs tabular-nums pointer-coarse:h-11 pointer-coarse:w-11',
                      'transition-colors',
                      isSelected
                        ? 'bg-primaria font-bold text-primaria-contraste'
                        : cn(
                            'hover:bg-superficie-suave active:bg-borda',
                            outside ? 'text-texto-tenue' : 'text-texto',
                            isToday &&
                              'font-bold text-primaria-texto ring-1 ring-inset ring-primaria-texto',
                          ),
                    )}
                  >
                    {Number(iso.slice(8))}
                  </button>
                </span>
              );
            })}
          </div>
        ))}
      </div>

      <div className="flex justify-end border-t border-borda pt-2">
        <button
          type="button"
          onClick={() => onSelect(today)}
          className="flex h-8 items-center rounded-borda px-2.5 text-xs font-semibold text-primaria-texto hover:bg-superficie-suave active:bg-borda pointer-coarse:h-11"
        >
          Hoje
        </button>
      </div>
    </div>
  );
}

function NavButton({
  label,
  icon,
  onClick,
}: {
  label: string;
  icon: 'seta-esquerda' | 'seta-direita';
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="flex h-8 w-8 items-center justify-center rounded-borda text-texto-suave hover:bg-superficie-suave hover:text-texto active:bg-borda pointer-coarse:h-11 pointer-coarse:w-11"
    >
      <Icon nome={icon} size={16} />
    </button>
  );
}
